/**
 * Wat er met een woning gebeurt tussen twee keer ophalen.
 *
 * Eén keer data binnenhalen geeft een foto van vandaag. Periodiek ophalen is pas
 * wat waard als je het verschil bewaart: een vraagprijs die zakt, een woning die
 * verdwijnt (lees: verkocht), een label dat achteraf wordt bijgesteld. De
 * `listing`-tabel overschrijft bij elke import - dat moet ook, hij hoort de
 * actuele stand te tonen - dus het verschil krijgt zijn eigen tabel.
 *
 * Hier staat alleen wat de database en de API allebei moeten weten: welke
 * statussen bestaan en welke velden de moeite van het volgen waard zijn.
 */
import { z } from 'zod';

/**
 * Staat een woning nog te koop?
 *
 * `verdwenen` en niet `verkocht`: we weten alleen dat hij niet meer in het aanbod
 * zat. Dat is meestal verkocht, maar soms teruggetrokken, opnieuw geplaatst onder
 * een nieuw nummer, of buiten het zoekgebied geraakt. De naam moet niet meer
 * beweren dan de waarneming draagt.
 */
export const WONINGSTATUSSEN = ['actief', 'verdwenen'] as const;

export const woningstatusSchema = z.enum(WONINGSTATUSSEN);
export type Woningstatus = z.infer<typeof woningstatusSchema>;

/**
 * De velden waarvan een wijziging wordt vastgelegd.
 *
 * Niet álle velden: een foto-URL die van CDN-pad wisselt of een pitch waar een
 * makelaar een komma in verzet, levert ruis op waar je nooit naar kijkt. Dit zijn
 * de velden waarvan een verandering iets betekent voor de vraag "is dit huis
 * interessanter geworden".
 *
 * `status` staat er tussen omdat de overgang actief -> verdwenen de belangrijkste
 * mutatie van allemaal is: dat is het moment waarop de markt antwoord geeft op
 * de vraagprijs.
 */
export const GEVOLGDE_VELDEN = [
  'vraagprijs',
  'prijsConditie',
  'oppervlakteM2',
  'kamers',
  'energielabel',
  'badge',
  'makelaar',
  'status',
] as const;

export type GevolgdVeld = (typeof GEVOLGDE_VELDEN)[number];

const GEVOLGD = new Set<string>(GEVOLGDE_VELDEN);

/** Of een veldnaam gevolgd wordt. Los van de array zodat de `Set` één keer bestaat. */
export function wordtGevolgd(veld: string): veld is GevolgdVeld {
  return GEVOLGD.has(veld);
}

/**
 * Eén waargenomen wijziging.
 *
 * Oude en nieuwe waarde zijn tekst, ook de vraagprijs. Eén tabel voor alle velden
 * houdt het aantal tabellen laag, en de enige plek waar het type ertoe doet - het
 * prijsverloop - vraagt expliciet om `veld = 'vraagprijs'` en kan daar zelf casten.
 * Een kolom per veldtype zou een migratie vragen bij elk veld dat we erbij willen.
 */
export const mutatieSchema = z.object({
  id: z.string().uuid(),
  waargenomenOp: z.string(),
  veld: z.enum(GEVOLGDE_VELDEN),
  oudeWaarde: z.string().nullable(),
  nieuweWaarde: z.string().nullable(),
});

export type Mutatie = z.infer<typeof mutatieSchema>;

/**
 * Het prijsverschil van een mutatie, of null als het er geen is.
 *
 * Negatief is een daling. Een woning van 550.000 die naar 525.000 gaat levert
 * -25000 op; dat is het getal waar je op wilt sorteren.
 */
export function prijsverschil(mutatie: Pick<Mutatie, 'veld' | 'oudeWaarde' | 'nieuweWaarde'>) {
  if (mutatie.veld !== 'vraagprijs') return null;
  const oud = Number(mutatie.oudeWaarde);
  const nieuw = Number(mutatie.nieuweWaarde);
  if (!Number.isFinite(oud) || !Number.isFinite(nieuw)) return null;
  return nieuw - oud;
}
